import { useEffect, useRef } from "react";

/**
 * Returns a ref to attach to an "end-of-list" sentinel element. Whenever the
 * sentinel scrolls into view *and* `enabled` is true, `onLoadMore` fires once.
 * The hook self-throttles via `enabled` — caller is responsible for flipping
 * it back on after each load completes.
 *
 * Usage:
 *   const ref = useInfiniteScroll({
 *     enabled: hasMore && !loadingMore,
 *     onLoadMore: () => fetchNextPage(),
 *     rootMargin: "200px",
 *   });
 *   return <>...<div ref={ref} /></>;
 */
export function useInfiniteScroll<T extends HTMLElement = HTMLDivElement>(opts: {
  enabled: boolean;
  onLoadMore: () => void;
  /** Pre-load before the sentinel is fully visible (default 200px below viewport). */
  rootMargin?: string;
}): React.RefObject<T | null> {
  const ref = useRef<T | null>(null);
  // Mirror the latest callback in a ref so the IntersectionObserver doesn't
  // need a fresh subscription each render.
  const onLoadMoreRef = useRef(opts.onLoadMore);
  onLoadMoreRef.current = opts.onLoadMore;

  useEffect(() => {
    const el = ref.current;
    if (!el || !opts.enabled) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            onLoadMoreRef.current();
            // Caller should flip `enabled` to false during loading;
            // we'll re-subscribe when it flips back.
            return;
          }
        }
      },
      { rootMargin: opts.rootMargin ?? "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [opts.enabled, opts.rootMargin]);

  return ref;
}
